/**
 * Initializes review video carousels that depend on the global Swiper build.
 */
(() => {
  const roots = () => document.querySelectorAll('[data-store-video-carousel]');

  const embedUrl = (id) =>
    `https://www.youtube.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0`;

  const syncNav = (root, swiper) => {
    const prev = root.querySelector('[data-store-vc-prev]');
    const next = root.querySelector('[data-store-vc-next]');
    if (!(prev instanceof HTMLElement) || !(next instanceof HTMLElement)) return;
    prev.setAttribute('aria-disabled', swiper.isBeginning ? 'true' : 'false');
    next.setAttribute('aria-disabled', swiper.isEnd ? 'true' : 'false');
  };

  const closeModal = (root) => {
    const dialog = root.querySelector('[data-store-vc-modal]');
    const iframe = root.querySelector('[data-store-vc-iframe]');
    if (iframe instanceof HTMLIFrameElement) iframe.src = '';
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
  };

  const openModal = (root, videoId) => {
    if (!videoId) return;
    const dialog = root.querySelector('[data-store-vc-modal]');
    const iframe = root.querySelector('[data-store-vc-iframe]');
    if (!(dialog instanceof HTMLDialogElement) || !(iframe instanceof HTMLIFrameElement)) return;
    iframe.src = embedUrl(videoId);
    if (typeof dialog.showModal === 'function') dialog.showModal();
  };

  const initModal = (root) => {
    const dialog = root.querySelector('[data-store-vc-modal]');
    if (!(dialog instanceof HTMLDialogElement)) return;

    root.querySelectorAll('[data-store-vc-open]').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      button.addEventListener('click', () => openModal(root, button.dataset.videoId || ''));
    });

    root.querySelector('[data-store-vc-close]')?.addEventListener('click', () => closeModal(root));
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeModal(root);
    });
    dialog.addEventListener('close', () => {
      const iframe = root.querySelector('[data-store-vc-iframe]');
      if (iframe instanceof HTMLIFrameElement) iframe.src = '';
    });
  };

  const initRoot = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.storeVcReady === 'true') return;

    initModal(root);

    const el = root.querySelector('[data-store-vc-swiper]');
    if (!(el instanceof HTMLElement) || el.swiper) {
      root.dataset.storeVcReady = 'true';
      return;
    }
    if (typeof window.Swiper !== 'function') return;

    const slides = Number(root.dataset.slides || '3');
    const space = Number(root.dataset.space || '20');
    const prev = root.querySelector('[data-store-vc-prev]');
    const next = root.querySelector('[data-store-vc-next]');
    const options = {
      slidesPerView: 1.2,
      spaceBetween: 10,
      watchOverflow: true,
      speed: 450,
      loop: root.dataset.loop === 'true',
      breakpoints: {
        640: {
          slidesPerView: Math.min(2, slides),
          spaceBetween: 15,
        },
        1024: {
          slidesPerView: slides,
          spaceBetween: space,
        },
      },
      navigation: {
        nextEl: next,
        prevEl: prev,
      },
      a11y: {
        prevSlideMessage: root.dataset.labelPrev || 'Previous',
        nextSlideMessage: root.dataset.labelNext || 'Next',
      },
      on: {
        init() {
          syncNav(root, this);
        },
        slideChange() {
          syncNav(root, this);
        },
        resize() {
          syncNav(root, this);
        },
      },
    };

    const autoplay = Number(root.dataset.autoplay || '0');
    if (autoplay > 0) {
      options.autoplay = { delay: autoplay, disableOnInteraction: false };
    }

    new window.Swiper(el, options);
    root.dataset.storeVcReady = 'true';
  };

  const initAll = () => {
    roots().forEach(initRoot);
  };

  const waitForSwiper = (attempt = 0) => {
    if (typeof window.Swiper === 'function') {
      initAll();
      return;
    }
    if (attempt > 40) return;
    window.setTimeout(() => waitForSwiper(attempt + 1), 50);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => waitForSwiper());
  } else {
    waitForSwiper();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (!(section instanceof HTMLElement)) return;
    const root = section.querySelector('[data-store-video-carousel]');
    if (root) {
      root.dataset.storeVcReady = 'false';
      waitForSwiper();
    }
  });
})();
